import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function TarifsPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Tarifs — Cabinet Joël Gautier, Avocat Paris 17',
      description: 'Honoraires transparents du Cabinet Gautier : consultation initiale à 150 €, honoraires au temps passé ou au forfait. Devis personnalisé sur demande.',
      url: `${siteUrl}/tarifs`,
      inLanguage: 'fr-FR',
      provider: {
        '@type': 'LegalService',
        name: 'Cabinet Joël Gautier',
        url: siteUrl,
        telephone: '+33183627658',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '91 boulevard Bessières',
          addressLocality: 'Paris',
          postalCode: '75017',
          addressCountry: 'FR',
        },
      },
      offers: [
        {
          '@type': 'Offer',
          name: 'Consultation Initiale',
          description: 'Première consultation de 30 minutes pour analyser votre situation juridique.',
          price: '150',
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
        },
        {
          '@type': 'Offer',
          name: 'Honoraire au temps passé',
          description: 'Facturation en fonction du temps effectivement consacré à votre dossier.',
          availability: 'https://schema.org/InStock',
        },
        {
          '@type': 'Offer',
          name: 'Honoraire au forfait',
          description: 'Montant global défini en amont pour l\'ensemble de la prestation.',
          availability: 'https://schema.org/InStock',
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-tarifs';
    script.textContent = JSON.stringify(schema);
    const existing = document.getElementById('schema-tarifs');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-tarifs');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/2457a24757fcc37e63ebfd614e72adca/b1558608be687494406c07fd3a70dac7.jpeg" alt="Maître Joël Gautier" className="h-28 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-900 hover:text-[#C9A961] transition-colors">Accueil</Link>
            <Link to="/tarifs" className="text-sm font-medium text-[#C9A961] transition-colors">Tarifs</Link>
            <Link to="/a-propos" className="text-sm font-medium text-gray-900 hover:text-[#C9A961] transition-colors">À propos</Link>
            <Link to="/actualites" className="text-sm font-medium text-gray-900 hover:text-[#C9A961] transition-colors">Actualités</Link>
            <Link to="/#contact" className="text-sm font-medium text-gray-900 hover:text-[#C9A961] transition-colors">Contact</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-6xl font-bold text-[#1A2B4A] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Nos Tarifs
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des honoraires transparents et adaptés à vos besoins. Première consultation à 150€ pour évaluer votre situation.
          </p>
        </div>
      </section>

      {/* Tarifs Principaux */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2B4A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Nos Honoraires</h2>
            <p className="text-lg text-gray-600">Tarifs adaptés à la complexité de votre dossier</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {/* Consultation Initiale */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0">
                <img src="https://public.readdy.ai/ai/img_res/f9db3fb2e6548f02307444e3ec414651.jpg" alt="Consultation" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B4A]/95 via-[#1A2B4A]/70 to-[#1A2B4A]/30"></div>
              </div>
              <div className="relative z-10 p-8 h-full flex flex-col justify-end min-h-[500px]">
                <div className="w-14 h-14 bg-[#C9A961] rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-chat-1-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Consultation Initiale</h3>
                <p className="text-white/80 text-sm mb-6">Première rencontre pour analyser votre situation juridique</p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-5xl font-bold text-[#C9A961]">150€</span>
                  <span className="text-white/70 text-sm">/ 30 min</span>
                </div>
                <Link to="/#contact" className="w-full py-3 bg-white text-[#1A2B4A] rounded-lg font-medium text-center hover:bg-[#C9A961] hover:text-white transition-all cursor-pointer whitespace-nowrap">
                  Prendre RDV
                </Link>
              </div>
            </div>

            {/* Dossier Simple */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0">
                <img src="https://readdy.ai/api/search-image?query=Clean%20organized%20legal%20documents%20and%20files%20on%20elegant%20wooden%20desk%20with%20soft%20natural%20lighting%20representing%20simple%20straightforward%20legal%20cases%20with%20professional%20minimalist%20aesthetic%20and%20gold%20accents&width=400&height=500&seq=tarif-simple-2&orientation=portrait" alt="Dossier Simple" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B4A]/95 via-[#1A2B4A]/70 to-[#1A2B4A]/30"></div>
              </div>
              <div className="relative z-10 p-8 h-full flex flex-col justify-end min-h-[500px]">
                <div className="w-14 h-14 bg-[#C9A961] rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-file-text-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Dossier Simple</h3>
                <p className="text-white/80 text-sm mb-6">Procédures standards et rédaction de documents</p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold text-[#C9A961]">Sur devis</span>
                </div>
                <Link to="/#contact" className="w-full py-3 bg-white text-[#1A2B4A] rounded-lg font-medium text-center hover:bg-[#C9A961] hover:text-white transition-all cursor-pointer whitespace-nowrap">
                  Demander un Devis
                </Link>
              </div>
            </div>

            {/* Dossier Complexe */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0">
                <img src="https://readdy.ai/api/search-image?query=Sophisticated%20law%20library%20with%20extensive%20legal%20books%20and%20complex%20case%20files%20on%20premium%20desk%20with%20dramatic%20lighting%20representing%20intricate%20legal%20matters%20requiring%20expert%20analysis%20with%20elegant%20gold%20details&width=400&height=500&seq=tarif-complexe-3&orientation=portrait" alt="Dossier Complexe" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B4A]/95 via-[#1A2B4A]/70 to-[#1A2B4A]/30"></div>
              </div>
              <div className="relative z-10 p-8 h-full flex flex-col justify-end min-h-[500px]">
                <div className="w-14 h-14 bg-[#C9A961] rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-briefcase-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Dossier Complexe</h3>
                <p className="text-white/80 text-sm mb-6">Affaires nécessitant une expertise approfondie</p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold text-[#C9A961]">Sur devis</span>
                </div>
                <Link to="/#contact" className="w-full py-3 bg-white text-[#1A2B4A] rounded-lg font-medium text-center hover:bg-[#C9A961] hover:text-white transition-all cursor-pointer whitespace-nowrap">
                  Demander un Devis
                </Link>
              </div>
            </div>

            {/* Accompagnement Permanent */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0">
                <img src="https://readdy.ai/api/search-image?query=Premium%20executive%20law%20office%20with%20modern%20technology%20and%20elegant%20furnishings%20representing%20ongoing%20legal%20counsel%20and%20partnership%20with%20sophisticated%20atmosphere%20warm%20lighting%20and%20luxurious%20gold%20accents&width=400&height=500&seq=tarif-permanent-4&orientation=portrait" alt="Accompagnement" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B4A]/95 via-[#1A2B4A]/70 to-[#1A2B4A]/30"></div>
              </div>
              <div className="relative z-10 p-8 h-full flex flex-col justify-end min-h-[500px]">
                <div className="w-14 h-14 bg-[#C9A961] rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Accompagnement Permanent</h3>
                <p className="text-white/80 text-sm mb-6">Conseil juridique continu pour entreprises</p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold text-[#C9A961]">Sur devis</span>
                </div>
                <Link to="/#contact" className="w-full py-3 bg-white text-[#1A2B4A] rounded-lg font-medium text-center hover:bg-[#C9A961] hover:text-white transition-all cursor-pointer whitespace-nowrap">
                  Nous Contacter
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informations Complémentaires */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A] rounded-3xl p-12 text-white">
            <h2 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>Informations Importantes</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <i className="ri-check-line text-[#C9A961] text-2xl flex-shrink-0"></i>
                <div>
                  <h3 className="font-bold text-lg mb-2">Première consultation à 150€</h3>
                  <p className="text-white/80">Rencontrez Maître Gautier pendant 30 minutes pour évaluer votre situation.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <i className="ri-check-line text-[#C9A961] text-2xl flex-shrink-0"></i>
                <div>
                  <h3 className="font-bold text-lg mb-2">Facilités de paiement</h3>
                  <p className="text-white/80">Des solutions de paiement échelonné sont possibles selon votre situation.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <i className="ri-check-line text-[#C9A961] text-2xl flex-shrink-0"></i>
                <div>
                  <h3 className="font-bold text-lg mb-2">Devis personnalisé</h3>
                  <p className="text-white/80">Chaque dossier est unique. Un devis détaillé vous sera remis après analyse de votre situation.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-[#1A2B4A] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Besoin d'un Devis Personnalisé ?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Contactez-nous pour obtenir une estimation précise adaptée à votre situation
          </p>
          <Link to="/#contact" className="inline-flex items-center gap-4 px-8 py-4 bg-[#1A2B4A] text-white rounded-full font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap">
            Demander un Devis
            <span className="w-10 h-10 bg-[#C9A961] rounded-full flex items-center justify-center">
              <i className="ri-arrow-right-line text-white"></i>
            </span>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1B2E] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <i className="ri-scales-line text-[#C9A961] text-[400px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></i>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <img src="https://static.readdy.ai/image/2457a24757fcc37e63ebfd614e72adca/b1558608be687494406c07fd3a70dac7.jpeg" alt="Maître Joël Gautier" className="h-20 mb-6" />
              <h3 className="text-4xl font-light mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Justice, Intégrité<br />
                & Excellence
              </h3>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <h4 className="text-base font-bold mb-4">Nos Services</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit des Affaires</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit Famille</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit Pénal</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Le Cabinet</h4>
                <ul className="space-y-2">
                  <li><Link to="/a-propos" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">À Propos</Link></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Équipe</a></li>
                  <li><Link to="/actualites" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Actualités</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Ressources</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">FAQ</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Guides Juridiques</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Blog</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Contact</h4>
                <ul className="space-y-2">
                  <li><Link to="/#contact" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Prendre RDV</Link></li>
                  <li><Link to="/#contact" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Nous Écrire</Link></li>
                  <li><Link to="/#contact" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Urgences</Link></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-t border-[#C9A961]/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <p className="text-sm text-white/70">© 2025 Cabinet Joël Gautier. Tous droits réservés.</p>
              <div className="flex gap-5">
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
              </div>
              <div className="flex gap-4 text-sm text-white/70">
                <a href="#" className="hover:text-[#C9A961] transition-colors cursor-pointer">Mentions Légales</a>
                <span>|</span>
                <a href="#" className="hover:text-[#C9A961] transition-colors cursor-pointer">Confidentialité</a>
                <span>|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A961] transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
